import { Component, DestroyRef, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { AlertService } from '@app/core';
import {
  AssessmentAlternative,
  AssessmentApplicationMode,
  AssessmentQuestionType,
  AssessmentWeightType,
} from '@app/core/models';
import { notNull, toLetter } from '@app/shared';
import { Popover, Strategy } from '@app/shared/modules/template/components/popover';
import { Store } from '@ngrx/store';
import { first, isEqual, isNil } from 'lodash';
import { distinctUntilChanged, Observable, take, tap } from 'rxjs';
import { ModalAuditActions, ModalAuditSelectors } from '../../store';
import {
  Application,
  AttemptQuestionData,
  AttemptStudentData,
  ModalAuditState,
  QuestionData,
} from '../../store/modal-audit.state';
import { onCalculateScaleScore, SCALES } from '../../utils';
import { PopoverAnswerOptionsComponent } from '../popover-answer-options/popover-answer-options.component';

@Component({
  selector: 'app-card-questions',
  templateUrl: './card-questions.component.html',
})
export class CardQuestionsComponent {
  public readonly assessmentQuestionTypeEnum = AssessmentQuestionType;
  public readonly assessmentWeightType = AssessmentWeightType;
  public readonly SCALES = SCALES;

  public questions$: Observable<QuestionData[]>;
  public application: Application;
  public randModeCtrl = new FormControl(undefined);
  private _student: AttemptStudentData;

  constructor(
    private _store: Store<ModalAuditState>,
    private _destroyRef: DestroyRef,
    private _alertService: AlertService,
    private _popover: Popover,
  ) {
    this.questions$ = this._store
      .select(ModalAuditSelectors.selectQuestions)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull(), distinctUntilChanged(isEqual));

    this._store
      .select(ModalAuditSelectors.selectApplication)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull(), distinctUntilChanged(isEqual))
      .subscribe((application) => (this.application = application));

    this._store
      .select(ModalAuditSelectors.selectStudentActive)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        notNull(),
        distinctUntilChanged(isEqual),
        tap((student) => {
          if (this.application.shuffle && student.index !== this._student?.index) {
            this.randModeCtrl.setValue(student.tentativa_modo_rand, { emitEvent: false });
            const method = student.is_answer_printed || isNil(student.is_answer_printed) ? 'enable' : 'disable';
            this.randModeCtrl[method]({ emitEvent: false });
            this._store.dispatch(
              ModalAuditActions.RefreshQuestionsRandModeEffect({
                rand_mode: student.tentativa_modo_rand,
              }),
            );
          }
        }),
      )
      .subscribe((student) => (this._student = student));

    this.randModeCtrl.valueChanges.subscribe((rand_mode) => {
      this._store.dispatch(
        ModalAuditActions.StudentLoadingReducer({
          attempt_uuid: this._student.tentativa_uuid,
          user_id: this._student.usuario_id,
          loading: true,
        }),
      );

      this._store.dispatch(
        ModalAuditActions.UpdateStudentRandModeEffect({
          attempt_uuid: this._student.tentativa_uuid,
          rand_mode,
        }),
      );

      this._store.dispatch(
        ModalAuditActions.RefreshQuestionsRandModeEffect({
          rand_mode,
        }),
      );
    });
  }

  public get studentAttemptUuid(): string {
    return this._student?.tentativa_uuid;
  }

  public get isAnswerPrinted(): boolean {
    return (
      !!this._student?.is_answer_printed ||
      (isNil(this._student?.is_answer_printed) && this.application.mode !== AssessmentApplicationMode.Online)
    );
  }

  public get isStudentLoading(): boolean {
    return this._student?.loading || false;
  }

  public get canShowQuestions(): boolean {
    return !this.application.shuffle || (this.application.shuffle && this.randModeCtrl.value);
  }

  public onSelectQuestion(question: QuestionData) {
    if (!!question.selected) {
      return;
    }
    this._store.dispatch(ModalAuditActions.UpdateQuestionActiveReducer({ question_uuid: question.uuid }));
  }

  public onGetAnswer(questionUuid: any): AttemptQuestionData {
    return first(
      this._student.tentativa_questoes?.filter(
        (tentativaQuestao: any) => tentativaQuestao.questao.uuid === questionUuid,
      ),
    );
  }

  public onGetLetter(index: number): string {
    return toLetter(index);
  }

  public trackByUuid = (index: any, data: any) => data.uuid;

  public onGetAlternativeClass(
    question: QuestionData,
    alternative: AssessmentAlternative,
    answer: AttemptQuestionData,
  ) {
    if (
      alternative.uuid === answer?.alternativa?.uuid &&
      (!!alternative.alternativa_correta || question.sociodemografica === 1)
    ) {
      return '!border-base-mantis-500 !text-base-mantis-500';
    }

    if (alternative.uuid !== answer?.alternativa?.uuid) {
      return '!text-base-gray-300';
    }

    if (alternative.uuid === answer?.alternativa?.uuid && !alternative.alternativa_correta) {
      return '!border-red-500 !text-red-500 !hover:border-red-700 !hover:!text-red-700';
    }
  }

  public onCalculateScaleScore(weight: number, scale: number): { score: number } {
    return onCalculateScaleScore(weight, scale);
  }

  public onSetAllBlank() {
    this._alertService
      .confirm({
        title: 'geral.atencao',
        message: 'gestor-avaliacoes.texto-todas-em-branco',
      })
      .pipe(notNull())
      .subscribe(() => {
        const data = { attempt_uuid: this._student.tentativa_uuid, user_id: this._student.usuario_id };
        this._store.dispatch(ModalAuditActions.StudentLoadingReducer({ ...data, loading: true }));
        this._store.dispatch(
          ModalAuditActions.UpdateStudentAllBlankEffect({
            ...data,
            application_uuid: this.application.uuid,
            rand_mode: this._student.tentativa_modo_rand,
          }),
        );
      });
  }

  public onSetAlternative(questionUuid: string, alternativeUuid: string, answer: AttemptQuestionData) {
    if (!!this._student.loading) {
      return;
    }

    if (answer?.alternativa?.uuid === alternativeUuid) {
      return;
    }

    this._alertService
      .confirm({
        title: 'geral.atencao',
        message: 'gestor-avaliacoes.trocar-alternativa',
      })
      .pipe(notNull())
      .subscribe(() => this.save(questionUuid, { alternative_uuid: alternativeUuid }));
  }

  public onSetScore(questionUuid: string, score: number, answer: AttemptQuestionData) {
    if (!!this._student.loading) {
      return;
    }

    if (score === answer?.nota) {
      return;
    }

    this._alertService
      .confirm({
        title: 'geral.atencao',
        message: 'geral.confirmacao',
      })
      .pipe(take(1), notNull())
      .subscribe(() => this.save(questionUuid, { score, feedback: answer?.feedback }));
  }

  public onGetAnswerAuditClass(answer: AttemptQuestionData): string {
    if (!!answer.anulado) {
      return 'text-danger';
    }

    if (!!answer.em_branco) {
      return 'text-info';
    }

    if (!!answer.alterado_por) {
      return 'text-warning';
    }

    return undefined;
  }

  public onCancelAnull(response: any, questionUuid: string) {
    this._store.dispatch(
      ModalAuditActions.UpdateAttemptQuestionReducer({
        response,
        data: {
          question_uuid: questionUuid,
          application_uuid: this.application.uuid,
          attempt_uuid: this.studentAttemptUuid,
          user_id: this._student.usuario_id,
        },
      }),
    );
  }

  public onOpenPopoverOptions(elementRef: ElementRef, question: AttemptQuestionData) {
    this._popover.open<any>(PopoverAnswerOptionsComponent, {
      positionStrategy: [Strategy.LEFT],
      elementRef,
      data: {
        question,
        question_uuid: question.uuid,
        application_uuid: this.application.uuid,
        attempt_uuid: this.studentAttemptUuid,
        user_id: this._student.usuario_id,
        rand_mode: this._student.tentativa_modo_rand,
      },
    });
  }

  public isNil(value: any): boolean {
    return isNil(value);
  }

  private save(questionUuid: string, data: { alternative_uuid?: string; score?: number; feedback?: string }) {
    if (!this._student.tentativa_uuid) {
      this._store.dispatch(
        ModalAuditActions.StudentLoadingReducer({
          attempt_uuid: this._student.tentativa_uuid,
          user_id: this._student.usuario_id,
          loading: true,
        }),
      );
    }
    this._store.dispatch(ModalAuditActions.QuestionLoadingReducer({ question_uuid: questionUuid, loading: true }));
    this._store.dispatch(
      ModalAuditActions.UpdateQuestionScoreEffect({
        ...data,
        application_uuid: this.application.uuid,
        user_id: this._student.usuario_id,
        attempt_uuid: this._student.tentativa_uuid,
        rand_mode: this._student.tentativa_modo_rand,
        question_uuid: questionUuid,
      }),
    );
  }
}
